import type { ValueExpression } from '../../../graphpad.model';
import type { FeatureStyleNode } from '../feature-style.model';

// =============================================================================
// PARAMS - Feature configuration from widget config
// =============================================================================

/** Intersection area feature parameters */
export interface IntersectionAreaParams {
  /** References to two parent function elements */
  of: [string, string];
  /** Start x-coordinate for the area (number or ValueExpression) */
  xStart: ValueExpression;
  /** End x-coordinate for the area (number or ValueExpression) */
  xEnd: ValueExpression;
  /** Whether to show signed areas (positive when f > g is green, negative when f < g is red) */
  signed?: boolean;
}

// =============================================================================
// DEFAULTS
// =============================================================================

/** Default parameter values (of, xStart and xEnd must be provided) */
export const INTERSECTION_AREA_DEFAULT_PARAMS: Omit<IntersectionAreaParams, 'of' | 'xStart' | 'xEnd'> = {
  signed: false,
};

/**
 * Default theme for intersection area feature.
 * Indigo for unsigned areas, green/red for signed areas.
 *
 * Structure:
 * - polygon: Fill style for the area
 * - line: Style for boundary lines at xStart and xEnd
 * - point: Style for points at the boundary line endpoints
 * - subparts.positive: Style for regions where f(x) > g(x)
 * - subparts.negative: Style for regions where f(x) < g(x)
 */
export const INTERSECTION_AREA_DEFAULTS: FeatureStyleNode = {
  polygon: {
    fillColor: 'indigo',
    fillOpacity: 0.3,
    strokeWidth: 0,
  },
  line: {
    color: 'indigo',
    dash: 3,
    strokeWidth: 2,
  },
  point: {
    color: 'indigo',
    visible: false,
  },
  subparts: {
    positive: {
      polygon: { fillColor: 'green' },
    },
    negative: {
      polygon: { fillColor: 'red' },
    },
  },
};
